import React, { useEffect, useState } from "react";
import { MdClose, MdArrowUpward, MdArrowDownward, MdEdit, MdDelete, MdLock, MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import {
  BoardColumn,
  BoardColumnCategory,
  BOARD_CATEGORY_LABELS,
} from "../../../../interfaces/ProjectTask";
import {
  createBoardColumn,
  updateBoardColumn,
  deleteBoardColumn,
  reorderBoardColumns,
} from "../../../../services/projectTaskApi";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";

interface Props {
  open: boolean;
  columns: BoardColumn[];
  onClose: () => void;
  /** Chamado após qualquer alteração (recarrega colunas + tarefas no pai). */
  onChanged: () => void;
}

// Categorias que o usuário pode escolher para colunas customizadas (Done/Cancelado são exclusivos do sistema).
const SELECTABLE_CATEGORIES: BoardColumnCategory[] = ["NotStarted", "Active", "OnHold"];

const categoryTone: Record<BoardColumnCategory, string> = {
  NotStarted: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  Active: "bg-dv-blue-100 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300",
  OnHold: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  Done: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Cancelled: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
};

interface DraftForm {
  name: string;
  category: BoardColumnCategory;
  color: string;
  showOnBoard: boolean;
}

const emptyDraft: DraftForm = { name: "", category: "Active", color: "#1466E3", showOnBoard: true };

const BoardColumnsModal: React.FC<Props> = ({ open, columns, onClose, onChanged }) => {
  const [items, setItems] = useState<BoardColumn[]>([]);
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<DraftForm>(emptyDraft);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItems([...columns].sort((a, b) => a.position - b.position));
    setEditingId(null);
  }, [columns, open]);

  if (!open) return null;

  const startEdit = (c: BoardColumn) => {
    setEditingId(c.id);
    setDraft({ name: c.name, category: c.category, color: c.color ?? "#1466E3", showOnBoard: c.showOnBoard });
  };

  const startNew = () => {
    setEditingId("new");
    setDraft(emptyDraft);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const save = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Informe o nome da coluna.");
      return;
    }
    setBusy(true);
    try {
      if (editingId === "new") {
        await createBoardColumn({ name, category: draft.category, color: draft.color, showOnBoard: draft.showOnBoard });
        toast.success("Coluna criada.");
      } else if (typeof editingId === "number") {
        await updateBoardColumn(editingId, { name, category: draft.category, color: draft.color, showOnBoard: draft.showOnBoard });
        toast.success("Coluna atualizada.");
      }
      cancelEdit();
      onChanged();
    } catch (e: any) {
      toast.error(mensagemDoServidor(e, "Erro ao salvar a coluna."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: BoardColumn) => {
    if (!window.confirm(`Excluir a coluna "${c.name}"?`)) return;
    setBusy(true);
    try {
      await deleteBoardColumn(c.id);
      toast.success("Coluna excluída.");
      onChanged();
    } catch (e: any) {
      toast.error(mensagemDoServidor(e, "Erro ao excluir a coluna."));
    } finally {
      setBusy(false);
    }
  };

  // Reordena localmente e persiste a nova ordem.
  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    setBusy(true);
    try {
      await reorderBoardColumns(next.map((c) => c.id));
      onChanged();
    } catch (e) {
      console.error("Erro ao reordenar colunas", e);
      toast.error("Erro ao reordenar as colunas.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";

  const renderForm = (isSystem: boolean) => (
    <div className="flex flex-col gap-3 rounded-xl border border-dv-blue-200 bg-dv-blue-50/40 p-3 dark:border-dv-blue-500/30 dark:bg-dv-blue-900/10">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Nome</label>
          <input
            className={inputCls}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Ex.: Blocked"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Categoria</label>
          <select
            className={inputCls}
            value={draft.category}
            disabled={isSystem}
            onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value as BoardColumnCategory }))}
          >
            {(isSystem ? ([draft.category] as BoardColumnCategory[]) : SELECTABLE_CATEGORIES).map((cat) => (
              <option key={cat} value={cat}>{BOARD_CATEGORY_LABELS[cat]}</option>
            ))}
          </select>
          {isSystem ? (
            <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">A categoria das colunas padrão é fixa.</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">
              “Parada” deixa a tarefa aberta, mas fora do capacity e do cronograma.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Cor</label>
          <input
            type="color"
            className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white dark:border-gray-600"
            value={draft.color}
            onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="h-4 w-4 accent-dv-blue-500"
            checked={draft.showOnBoard}
            onChange={(e) => setDraft((d) => ({ ...d, showOnBoard: e.target.checked }))}
          />
          Mostrar como coluna no board
        </label>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={cancelEdit}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded-lg bg-ink-900 px-4 py-1.5 text-xs font-bold text-white shadow-dv-md hover:bg-ink-800 disabled:opacity-50 dark:bg-white dark:text-ink-900"
          >
            {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Modal onClose={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Colunas do board</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-gray-700 transition-colors hover:bg-gray-100 hover:text-navy-700 dark:text-gray-300 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">
          Crie e reordene as etapas do kanban (ex.: <span className="font-semibold">Blocked</span>). A categoria define como a
          coluna se comporta nas regras de capacity, cronograma e atraso.
        </p>

        <div className="flex flex-col gap-2">
          {items.map((c, i) => (
            <div key={c.id}>
              {editingId === c.id ? (
                renderForm(c.isSystem)
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-white/5 dark:bg-navy-900">
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c.color ?? "#94a3b8" }} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-navy-700 dark:text-white" title={c.name}>{c.name}</span>
                      {c.isSystem && <MdLock className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Coluna padrão" />}
                      {!c.showOnBoard && (
                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                          oculta no board
                        </span>
                      )}
                    </div>
                    <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${categoryTone[c.category]}`}>
                      {BOARD_CATEGORY_LABELS[c.category]}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || busy} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30 dark:hover:bg-navy-700" aria-label="Mover para cima">
                      <MdArrowUpward className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1 || busy} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30 dark:hover:bg-navy-700" aria-label="Mover para baixo">
                      <MdArrowDownward className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => startEdit(c)} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 hover:text-dv-blue-600 dark:hover:bg-navy-700" aria-label="Editar">
                      <MdEdit className="h-4 w-4" />
                    </button>
                    {!c.isSystem && (
                      <button type="button" onClick={() => remove(c)} disabled={busy} className="rounded-md p-1.5 text-gray-500 hover:bg-red-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-900/30" aria-label="Excluir">
                        <MdDelete className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          {editingId === "new" ? (
            renderForm(false)
          ) : (
            <button
              type="button"
              onClick={startNew}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:border-dv-blue-400 hover:bg-dv-blue-50/40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              <MdAdd className="h-4 w-4" aria-hidden="true" /> Nova coluna
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BoardColumnsModal;
